/**
 * @file WidthField.tsx
 * @description Ширина секции числом (phase19.16, партия SB120, этап 1;
 * uiux-sb120-stage1 «Дополнение» §2.1; данные db-design-sb120-stage1 §1
 * `look.container`): 320–1920 px, подсказки «По сетке 1280» и «Узкая 768»,
 * отдельная кнопка «Во всю ширину» (значение `"full"` — без предела и боковых
 * полей, не 1920). Нет ключа — по сетке 1280; 1280 из ползунка тоже пишется
 * как «по сетке». Три кнопки равноправны (QA D2): «По сетке 1280» / «Узкая
 * 768» и ползунок снимают «Во всю» и ставят число. Под полем — «Содержимое ≈ … px».
 * SB120 этап 2 (uiux-sb120-stage2 §3): подпись без «окна» — при рамке сайта
 * «По центру» рядом «в рамке N px»; во всё окно — только с «Фоном за рамку».
 */

import { SECTION_LOOK_LIMITS, SITE_THEME_LIMITS, type SectionLook } from "@chaiberi/shared";
import { Maximize2 } from "lucide-react";

import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { useSectionForm } from "../section-form-context";
import { contentWidthHint } from "../../../../lib/site-builder/look-size";
import { isOutsideFrame } from "../../../../lib/site-builder/look-outside";

const { container: C } = SECTION_LOOK_LIMITS;

const PRESETS = [
  { label: `По сетке ${C.grid}`, value: C.grid },
  { label: `Узкая ${C.narrow}`, value: C.narrow },
];

interface Props {
  look: SectionLook;
  onChange: (look: SectionLook) => void;
}

export function WidthField({ look, onChange }: Props) {
  const { preview, theme } = useSectionForm();
  const full = look.container === "full";
  // «Во всю ширину» при рамке «По центру» — во всю рамку, а не окно (SB120 этап 2, uiux-sb120-stage2 §3, факт 1).
  const frame = theme?.frame;
  const framed = frame?.mode === "boxed" && !isOutsideFrame(look, frame) ? frame.maxWidth : null;
  const set = (container: SectionLook["container"]) => {
    const next = { ...look };
    // По сетке — нет ключа (как у сайта).
    if (container === undefined || container === C.grid) delete next.container;
    else next.container = container;
    onChange(next);
  };

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2">
        <button
          type="button"
          aria-pressed={full}
          onClick={() => set(full ? undefined : "full")}
          className="inline-flex h-12 items-center gap-2 self-start rounded-lg bg-surface-low px-3 text-sm font-medium text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-ring aria-pressed:[box-shadow:inset_0_0_0_2px_var(--primary)] can-hover:h-8"
        >
          <Maximize2 aria-hidden className="size-4" />
          Во всю ширину
        </button>
        {framed !== null ? <span className="type-caption text-on-surface-variant">в рамке {framed} px</span> : null}
      </div>
      <NumberSliderField
        label="Ширина блока"
        value={typeof look.container === "number" ? look.container : undefined}
        inherit={full ? { label: "во всю" } : { value: C.grid, label: "по сетке 1280" }}
        min={C.min}
        max={C.max}
        step={1}
        largeStep={40}
        unit="px"
        display={{ factor: 1, decimals: 2 }}
        presets={PRESETS}
        hint={contentWidthHint(look, theme?.spacing.density ?? SITE_THEME_LIMITS.density.default, frame)}
        onChange={set}
        onPreview={(v) => preview("look.container", v ?? C.grid)}
      />
    </div>
  );
}
